import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MessageSquareIcon, LayoutIcon, PencilIcon, CheckCircleIcon, RocketIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export function WhyUs() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  const {
    t
  } = useLanguage();
  const steps = [{
    icon: MessageSquareIcon,
    title: t('how.step1.title'),
    note: t('how.step1.note'),
    number: '01'
  }, {
    icon: LayoutIcon,
    title: t('how.step2.title'),
    note: t('how.step2.note'),
    number: '02'
  }, {
    icon: PencilIcon,
    title: t('how.step3.title'),
    note: t('how.step3.note'),
    number: '03'
  }, {
    icon: CheckCircleIcon,
    title: t('how.step4.title'),
    note: t('how.step4.note'),
    number: '04'
  }, {
    icon: RocketIcon,
    title: t('how.step5.title'),
    note: t('how.step5.note'),
    number: '05'
  }];
  return <section ref={ref} className="relative w-full overflow-hidden px-4 py-16 sm:py-24" style={{
    backgroundColor: 'var(--bg-primary)'
  }} data-id="element-339">
      <div className="relative z-10 mx-auto max-w-6xl" data-id="element-340">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="mb-12 sm:mb-16 text-center" data-id="element-341">
          <h2 className="text-3xl md:text-4xl" style={{
          color: 'var(--text-primary)'
        }} data-id="element-342">
            {t('how.title')} {t('how.titleHighlight')}
          </h2>
        </motion.div>

        {/* Staircase Layout - Starting from RIGHT */}
        <div className="relative flex flex-col gap-8 sm:gap-12" data-id="element-343">
          {steps.map((step, index) => {
          // Start from right (index 0 = right, index 1 = left, etc.)
          const isRight = index % 2 === 0;
          return <motion.div key={index} initial={{
            opacity: 0,
            x: isRight ? 30 : -30,
            y: 20
          }} animate={isInView ? {
            opacity: 1,
            x: 0,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.2 + index * 0.12,
            type: 'spring',
            stiffness: 80
          }} className={`relative ${isRight ? 'sm:ml-auto sm:mr-0' : 'sm:mr-auto sm:ml-0'} sm:w-[85%]`} data-id="element-344">
                <div className="rounded-3xl border p-6 sm:p-8 relative overflow-hidden" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-secondary)'
            }} data-id="element-345">
                  {/* Step number watermark — hidden on mobile */}
                  <div className="absolute top-4 right-6 text-8xl font-bold opacity-[0.03] hidden sm:block" style={{
                color: 'var(--text-primary)'
              }} data-id="element-346">
                    {step.number}
                  </div>

                  {/* Content */}
                  <div className="flex gap-4 sm:gap-6 relative z-10" data-id="element-347">
                    {/* Icon */}
                    <motion.div initial={{
                  scale: 0
                }} animate={isInView ? {
                  scale: 1
                } : {}} transition={{
                  duration: 0.4,
                  delay: 0.4 + index * 0.12,
                  type: 'spring'
                }} className="flex-shrink-0" data-id="element-348">
                      <div className="flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-2xl" style={{
                    backgroundColor: 'var(--bg-tertiary)'
                  }} data-id="element-349">
                        <step.icon className="h-7 w-7 sm:h-8 sm:w-8" style={{
                      color: 'var(--accent)'
                    }} data-id="element-350" />
                      </div>
                    </motion.div>

                    {/* Text */}
                    <div className="flex-1 pt-1" data-id="element-351">
                      <h3 className="mb-2 text-base sm:text-lg font-medium leading-tight" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-352">
                        {step.title}
                      </h3>
                      <p className="text-sm sm:text-base" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-353">
                        {step.note}
                      </p>
                    </div>
                  </div>

                  {/* Animated dot indicator */}
                  <motion.div initial={{
                scale: 0
              }} animate={isInView ? {
                scale: [0, 1.3, 1]
              } : {}} transition={{
                duration: 0.5,
                delay: 0.5 + index * 0.12
              }} className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full ${isRight ? '-left-1.5 sm:-left-6' : '-right-1.5 sm:-right-6'} hidden sm:block`} style={{
                backgroundColor: 'var(--accent)'
              }} data-id="element-354" />
                </div>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}